import { FC, ReactNode, useEffect, useState } from 'react';
import Image from 'next/legacy/image';
import { useTranslation } from 'next-i18next';

import { containerQueries } from '@phoenix/design-system/frontend/ui/breakpoints/breakpoints';

import { ImageViewer } from '../image-viewer/image-viewer';
import { ImageCrop } from '../image-cropper/image-crop';
import { ImageCropProvider } from '../image-cropper/use-image-crop-context';

import { ImageThumbnail } from './image-thumbnail';
import { ImagePlaceholder } from './image-placeholder';
import { Buttons } from './buttons';
import { DeleteConfirmationDialog } from './delete-confirmation-dialog';

export enum FileUploadStatus {
  Uploading = 'uploading',
  Uploaded = 'uploaded',
  Error = 'error',
}

export type GalleryImage = {
  id: string;
  url: string;
  thumbnail?: string;
  status: FileUploadStatus;
  errorCode?: string;
  isDeletable: boolean;
  isMainImage: boolean;
  isSystem?: boolean;
  originalFilename?: string;
};

interface ImagesGalleryProps {
  images: GalleryImage[];
  imageViewerTitle: string;
  className?: string;
  placeholderImgClassName?: string;
  isUploading?: boolean;
  maxSize?: number;
  maxImages?: number;
  galleryErrorMessage?: string;
  onAddImage: (file: File) => void;
  onDeleteImage: (imageId: string) => void;
  onSetMainImage?: (imageId: string) => void;
  customImageRender?: (image: GalleryImage) => ReactNode;
  withCropper?: boolean;
}

const DEFAULT_MAX_IMAGES = 3;
const DEFAULT_MAX_SIZE_MB = 7;
export const DEFAULT_MAX_SIZE_BYTES = DEFAULT_MAX_SIZE_MB * 1024 * 1024;

export const ImagesGallery: FC<React.PropsWithChildren<ImagesGalleryProps>> = ({
  images,
  imageViewerTitle,
  className,
  isUploading = false,
  maxImages = DEFAULT_MAX_IMAGES,
  onAddImage,
  onDeleteImage,
  onSetMainImage,
  customImageRender,
}) => {
  const { t } = useTranslation('shared');

  const [selectedImage, setSelectedImage] = useState<GalleryImage | undefined>();
  const [isImageViewOpen, setIsImageViewOpen] = useState(false);
  const [isImageUnsupported, setIsImageUnsupported] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    setIsImageUnsupported(false);
  }, [selectedImage?.id]);

  const maxLimitReached = images.length >= maxImages;

  useEffect(() => {
    setSelectedImage(images.find((img) => img.isMainImage));
  }, [images]);

  const openSlider = () => {
    setIsImageViewOpen(true);
  };

  const handleDeleteImage = (imageId: string) => {
    if (imageId === selectedImage?.id) {
      let nextId: string;
      const index = images.findIndex((image) => image.id === selectedImage.id);

      if (images.length > 1) {
        switch (index) {
          case images.length - 1:
            nextId = images[index - 1].id;
            break;
          default:
            nextId = images[index + 1].id;
        }

        const newSelectedImage = images.find((image) => image.id === nextId);
        if (newSelectedImage) {
          setSelectedImage(newSelectedImage);
        }
      } else {
        setSelectedImage(images[0]);
      }
    }
    onDeleteImage(imageId);
  };

  const onDeleteConfirmation = () => {
    handleDeleteImage(selectedImage?.id ?? '');
    setShowDeleteConfirmation(false);
  };

  return (
    <ImageCropProvider>
      <div className={className} data-testid="images-gallery">
        {selectedImage?.url && !isImageUnsupported ? (
          <>
            <div className="relative aspect-square">
              <button
                type="button"
                className="block w-full h-full focus-visible:outline outline-primary-black outline-2 outline-offset-2"
                onClick={!isUploading ? openSlider : undefined}
                aria-haspopup="dialog"
                data-testid="gallery-picture-button"
              >
                <Image
                  src={selectedImage.url}
                  alt={
                    selectedImage.id === 'system-image-id'
                      ? `${t('shared_imageUpload_system_alt')} (${selectedImage.originalFilename})`
                      : `${t('shared_imageUpload_custom_alt')} (${selectedImage.originalFilename})`
                  }
                  layout="fill"
                  objectFit="contain"
                  priority={true}
                  onError={() => setIsImageUnsupported(true)}
                  loader={() => selectedImage.url}
                  sizes={`(max-width: ${containerQueries.tablet}) 100vw, (max-width: ${containerQueries.desktop}) 50vw`}
                  data-testid={`gallery-picture_${selectedImage.id}`}
                />
              </button>
              <Buttons
                className="absolute bottom-5 left-5 gap-y-2"
                selectedImage={selectedImage}
                showMainImageButton={images.length > 1}
                onSetMainImage={onSetMainImage}
                setShowDeleteConfirmation={() => setShowDeleteConfirmation(true)}
                deleteBtnDataTestId="img-gallery-delete-btn"
              />
            </div>
            <ImageViewer
              dialogIsOpen={isImageViewOpen}
              closeDialog={() => setIsImageViewOpen(false)}
              initiallySelectedImage={selectedImage}
              title={imageViewerTitle}
              images={images}
              onSetMainImage={onSetMainImage}
              onDeleteImage={handleDeleteImage}
            />
          </>
        ) : (
          <ImagePlaceholder />
        )}
        <div className="mt-4 flex flex-wrap justify-start gap-[1.125rem]">
          {!maxLimitReached && <ImageCrop onDone={onAddImage} />}
          {!!images.length &&
            images
              .filter((image) => image.status !== 'error')
              .map((image) => (
                <ImageThumbnail
                  image={image}
                  key={image.id}
                  selectedImageId={selectedImage?.id}
                  customImageRender={customImageRender}
                  onClick={image.status !== 'uploading' ? () => setSelectedImage(image) : undefined}
                />
              ))}
        </div>
        <span className="mt-4 block text-secondary-dark stihl-text-body-xs-reg">
          {t(maxLimitReached ? 'shared_imageUpload_label_maximumAmount' : 'shared_imageUpload_info_formatSize')}
        </span>
      </div>
      <DeleteConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
        }}
        onConfirm={onDeleteConfirmation}
        filename={selectedImage?.originalFilename}
      />
    </ImageCropProvider>
  );
};
